import { landingImages } from './landingVisual'

/**
 * Design B — "the digital world of campus life" — photography and
 * demonstration content.
 *
 * Every entry is either a real image asset or content explicitly marked as
 * DEMONSTRATION. Nothing here is presented on the page as a platform total, a
 * partnership or a verified real-world claim. Campus availability is read from
 * the campus register, never from this file.
 */

export const landingBImages = {
  /** Full-bleed hero: campus walkway, student in Campuzon green. */
  hero: 'https://cdn.magicpatterns.com/patterns/generated-images/fddf3872-6d2f-4f3a-a79b-7c407514d149.jpg',
  learn:
    'https://cdn.magicpatterns.com/patterns/generated-images/cc8e568a-70d9-43af-a857-b67ee4184096.jpg',
  live: 'https://cdn.magicpatterns.com/patterns/generated-images/2b85f528-929a-4e34-90e3-e526e28efa84.jpg',
  connect:
    'https://cdn.magicpatterns.com/patterns/generated-images/b49f9c71-bc33-455d-b3aa-74f4175d069d.jpg',
  discover:
    'https://cdn.magicpatterns.com/patterns/generated-images/346033c7-df47-44cf-b55a-dfa44ae7c0e9.jpg',
  earn: 'https://cdn.magicpatterns.com/patterns/generated-images/a5d16072-f65b-4d78-ae04-8f039ad4cb2b.jpg',
  grow: 'https://cdn.magicpatterns.com/patterns/generated-images/cc62a2b0-9798-472f-a328-1a382c9d95ca.jpg',
  meetCampus:
    'https://cdn.magicpatterns.com/patterns/generated-images/476518cd-4b15-43f8-9de6-e5deff2b9aa4.jpg',
  events:
    'https://cdn.magicpatterns.com/patterns/generated-images/075bb9d0-4365-4e0d-b700-9b5df0e887d5.jpg',
  ctaGroup:
    'https://cdn.magicpatterns.com/patterns/generated-images/9f82c542-aa89-488f-9ed9-735cf57c0b73.jpg',
  /** Shared with the other homepage experience — one product, one library. */
  marketplace: landingImages.marketplace,
  housing: landingImages.housing,
  academics: landingImages.academics,
  campusBuilding: landingImages.campusBuilding,
  identityPortrait: landingImages.identityPortrait,
} as const

/** Assets warmed before this experience is presented, so nothing pops in. */
export const landingBCriticalAssets: readonly string[] = [
  landingBImages.hero,
  landingBImages.learn,
  landingBImages.live,
  landingBImages.connect,
  landingBImages.meetCampus,
  landingBImages.ctaGroup,
]

export interface LifeFacet {
  id: string
  word: string
  image: string
  alt: string
  /** Where in Campuzon this part of campus life actually happens. */
  to: string
}

/** The visual sequence: campus life, not a feature grid. */
export const lifeFacets: readonly LifeFacet[] = [
  {
    id: 'learn',
    word: 'Learn',
    image: landingBImages.learn,
    alt: 'A student studying at a desk with a notebook and laptop',
    to: '/academics',
  },
  {
    id: 'live',
    word: 'Live',
    image: landingBImages.live,
    alt: 'A modern student residence building with balconies and greenery',
    to: '/housing',
  },
  {
    id: 'connect',
    word: 'Connect',
    image: landingBImages.connect,
    alt: 'Two students talking and laughing together on campus',
    to: '/social',
  },
  {
    id: 'discover',
    word: 'Discover',
    image: landingBImages.discover,
    alt: 'A student eating a meal outdoors in a campus courtyard',
    to: '/market',
  },
  {
    id: 'earn',
    word: 'Earn',
    image: landingBImages.earn,
    alt: 'A student running a small campus stall of food and produce',
    to: '/market/gigs',
  },
  {
    id: 'grow',
    word: 'Grow',
    image: landingBImages.grow,
    alt: 'A student walking across campus with a backpack',
    to: '/opportunities',
  },
]

export interface DayMoment {
  time: string
  label: string
  icon: 'class' | 'library' | 'lunch' | 'assignment' | 'event' | 'marketplace'
  /** The Campuzon capability this moment belongs to. */
  to: string
}

/**
 * DEMONSTRATION day. It shows how the parts of Campuzon meet in one day; it is
 * not a real student's schedule and is never presented as one.
 */
export const demoDay: readonly DayMoment[] = [
  { time: '07:30', label: 'Class', icon: 'class', to: '/academics' },
  { time: '10:00', label: 'Library', icon: 'library', to: '/academics' },
  { time: '13:00', label: 'Lunch', icon: 'lunch', to: '/market/food' },
  { time: '15:30', label: 'Assignment', icon: 'assignment', to: '/academics' },
  { time: '17:00', label: 'Campus event', icon: 'event', to: '/community' },
  { time: '20:00', label: 'Marketplace', icon: 'marketplace', to: '/market' },
]

/** DEMONSTRATION courses rendered inside the academics product preview. */
export const demoCourses = [
  { code: 'SYE 305', title: 'Systems Engineering', progress: 75 },
  { code: 'MTH 301', title: 'Engineering Maths', progress: 60 },
  { code: 'EEE 312', title: 'Digital Electronics', progress: 80 },
] as const

/** DEMONSTRATION tasks rendered inside the academics product preview. */
export const demoTasks = [
  { title: 'Assignment', detail: 'Digital Logic', due: 'Due tomorrow', urgent: true },
  { title: 'Test', detail: 'Engineering Maths', due: 'May 24', urgent: false },
  { title: 'Lab report', detail: 'Circuit analysis', due: 'May 26', urgent: false },
] as const

/** DEMONSTRATION marketplace listings rendered inside the preview. */
export const demoListings = [
  { title: 'Jollof & chicken', meta: 'Campus Bites', price: '₦2,500', rating: 4.8 },
  { title: 'Engineering textbook', meta: 'Second hand', price: '₦9,000', rating: 4.6 },
  { title: 'Laundry service', meta: 'Per load', price: '₦1,800', rating: 4.7 },
  { title: 'Phone charger', meta: 'New', price: '₦3,000', rating: 4.5 },
] as const

/** DEMONSTRATION events rendered inside the campus preview. */
export const demoEvents = [
  { title: 'Engineering Week', date: 'Mon 17 Aug', venue: 'Main Auditorium' },
  { title: 'Sports Fiesta', date: 'Sat 22 Aug', venue: 'Sports Complex' },
  { title: 'Career Fair', date: 'Thu 27 Aug', venue: 'Students Centre' },
  { title: 'Design Talk', date: 'Fri 28 Aug', venue: 'CIT 105' },
] as const

/** DEMONSTRATION personalised day rendered in the personalisation preview. */
export const demoFocus = [
  { time: '09:00', title: 'Systems Engineering', note: 'Lecture · LT 100', now: true },
  { time: '12:30', title: 'Recommended lunch nearby', note: 'Campus Bites · 4 min', now: false },
  { time: '15:00', title: 'Engineering Society', note: 'Meeting · CIT 105', now: false },
  { time: '18:00', title: 'Career talk: tech roles', note: 'Students Centre', now: false },
] as const

/** DEMONSTRATION recommendations rendered in the personalisation preview. */
export const demoRecommendations = [
  {
    kind: 'Marketplace',
    title: 'Engineering drawing set',
    price: '₦7,500',
    image: landingBImages.academics,
  },
  {
    kind: 'Housing',
    title: 'University Courts',
    price: '₦185,000 / yr',
    image: landingBImages.housing,
  },
  {
    kind: 'Opportunity',
    title: 'Data analyst intern',
    price: 'Remote',
    image: landingBImages.campusBuilding,
  },
  {
    kind: 'Marketplace',
    title: 'Drawing board',
    price: '₦3,200',
    image: landingBImages.marketplace,
  },
] as const
